import { useEffect, useRef, useState } from 'react'
import { CollapsedNavTooltip } from '../CollapsedNavTooltip'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Menu,
  Building2,
  Layers,
  Users,
  CreditCard,
  ShieldCheck,
  Activity,
  Database,
  Mail,
  ScrollText,
  Ticket,
  LifeBuoy,
  Brain,
  HardDrive,
  FileBarChart,
  BarChart3,
  ChevronDown,
  History,
  Wallet,
  Gift,
  Gauge,
  Megaphone,
  Package,
  UserPlus,
  Footprints,
  Timer,
  BookOpen,
  Plug,
  Settings as SettingsIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Logo } from '../Logo'
import { BrandMarkIcon } from '../BrandMarkIcon'
import { UserMenu } from '../UserMenu'
import { NotificationsBell } from '../NotificationsBell'
import { FontSizeMenu } from '../FontSizeMenu'
import { ThemeToggle } from '../ui/ThemeToggle'
import { DateTimeClock } from '../DateTimeClock'
import { useAuthStore } from '../../stores/authStore'
import { useSidebarCollapsed, useSidebarStore } from '../../stores/sidebarStore'
import { SidebarToggle } from './SidebarToggle'

/** Sidebar mirrors the SaaS-platform standard (Companies / Plans / Users
 *  / Payments / Invoices / Logs / Coupons / Support Tickets /
 *  Settings) so operators familiar with that pattern feel at home
 *  immediately.
 *
 *  Three collapsible dropdowns club related surfaces so the rail stays
 *  short rather than a long flat list:
 *    - "Billing" — Plans, Payments, Invoices, Coupons.
 *    - "Usage"   — AI Usage, Storage, Usage Report.
 *    - "Logs"    — Audit Logs, Login Activity, Email Status Logs.
 *  An item with a `children` array renders as that dropdown via
 *  `NavDropdown`; a plain item renders as a single `NavLeaf` row. */
const NAV = [
  {
    items: [
      {
        to: '/super-admin/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        end: true,
      },
      { to: '/super-admin/analytics', label: 'Analytics', icon: BarChart3 },
      { to: '/super-admin/companies', label: 'Companies', icon: Building2 },
      { to: '/super-admin/all-users', label: 'All Users', icon: Users },
      // Billing — Plans, Payments, Invoices and Coupons share one
      // collapsible dropdown so the money/billing surfaces sit together
      // rather than scattered down the list. Auto-opens when the current
      // route is one of its children.
      {
        label: 'Billing',
        icon: Wallet,
        children: [
          { to: '/super-admin/plans', label: 'Plans', icon: Layers },
          { to: '/super-admin/payments', label: 'Payments', icon: CreditCard },
          { to: '/super-admin/coupons', label: 'Coupons', icon: Ticket },
          { to: '/super-admin/referrals', label: 'Referrals', icon: Gift },
        ],
      },
      // Usage — AI Usage, Storage and Usage Report share one collapsible
      // dropdown so the metering / consumption surfaces sit together.
      {
        label: 'Usage',
        icon: Gauge,
        children: [
          { to: '/super-admin/ai-usage', label: 'AI Usage', icon: Brain },
          { to: '/super-admin/storage', label: 'Storage', icon: HardDrive },
          {
            to: '/super-admin/usage-report',
            label: 'Usage Report',
            icon: FileBarChart,
          },
        ],
      },
      // Logs — Audit, Login Activity, Visitor Logs and Email Status share
      // one collapsible dropdown so the read-only history surfaces sit
      // together rather than scattered down the list. The dropdown
      // auto-opens when the current route is one of its children.
      {
        label: 'Logs',
        icon: History,
        children: [
          { to: '/super-admin/audit-logs', label: 'Audit Logs', icon: ShieldCheck },
          { to: '/super-admin/login-activity', label: 'Login Activity', icon: Activity },
          { to: '/super-admin/visitor-logs', label: 'Visitor Logs', icon: Footprints },
          // Page Performance — how fast every page opens, per company.
          { to: '/super-admin/page-performance', label: 'Page Performance', icon: Timer },
          { to: '/super-admin/email-logs', label: 'Email Status Logs', icon: ScrollText },
        ],
      },
      // Notifications has NO sidebar row — it's reached from the header
      // bell's "View all notifications" link. (Removed by request across
      // all panels.)
      {
        to: '/super-admin/email-templates',
        label: 'Email Templates',
        icon: Mail,
      },
      // Leads — inbound enquiries from the marketing-site Contact form
      // (source = "website") plus manually-added sales leads.
      { to: '/super-admin/leads', label: 'Leads', icon: UserPlus },
      {
        to: '/super-admin/support-tickets',
        label: 'Support Tickets',
        icon: LifeBuoy,
      },
      // Data Platforms — read-only cross-tenant analytics gateway for external
      // data platforms (SoftwareWatch: consumers + scoped API keys).
      {
        to: '/super-admin/data-platforms',
        label: 'Data Platforms',
        icon: Database,
      },
      // [Servexa] Support/Feature integration config — its own dedicated page
      // (spec A20), never folded into a generic settings screen.
      {
        to: '/super-admin/servexa-integration',
        label: 'Servexa Integration',
        icon: Plug,
      },
      // [Telegram bot] The platform's single bot connection (token + webhook).
      // Companies never configure a bot of their own — they only connect
      // individual members from their own Telegram page.
      {
        to: '/super-admin/telegram-integration',
        label: 'Telegram Integration',
        icon: Plug,
      },
      // SOP Integration is hidden from the Super Admin sidebar — the
      // page itself stays mounted at /super-admin/sop-integration in
      // case it needs to be reached directly during debugging, but
      // it's no longer surfaced in the nav. Re-add this entry if the
      // page needs to be reachable from the sidebar again.
      // { to: '/super-admin/sop-integration', label: 'SOP Integration', icon: Plug },
      // Demo Data — module removed 2026-07 per developer directive
      // (bug 0011626). No sidebar entry, no route, no page component,
      // no backend module. The tenant-management page is the canonical
      // way to work with demo companies now (the `isDemo` flag on
      // Tenant still exists so tenants that were seeded via the old
      // demo tooling stay identifiable in the Companies list).
      // Maintenance + IP Access both moved into the Settings page (their
      // own sections) — the standalone nav entries + routes were removed
      // in favour of the single Settings surface. `/super-admin/maintenance`
      // and `/super-admin/ip-access` now redirect to Settings for old
      // bookmarks. Usage Report moved into the "Usage" dropdown above.
      // Announcements — Super Admin broadcasts a message to every company;
      // recipients see it in their notification bell.
      {
        to: '/super-admin/announcements',
        label: 'Announcements',
        icon: Megaphone,
      },
      // Our Products — the shared hosted products feed, embedded for the
      // operator too (every human-facing role can reach it).
      {
        to: '/super-admin/our-products',
        label: 'Our Products',
        icon: Package,
      },
      // Settings — placed last in the rail per the requested layout.
      { to: '/super-admin/settings', label: 'Settings', icon: SettingsIcon },
      // User Manual — the platform operator's guide. Kept as the very last
      // item in the rail per the User Manual requirement.
      { to: '/super-admin/user-manual', label: 'User Manual', icon: BookOpen },
    ],
  },
]

/** Single nav row. `indent` shifts the icon + label right so a child
 *  inside a dropdown reads as nested under its parent. Collapsed mode
 *  centres the icon and drops the label + indent (the 68px rail has no
 *  room for either).
 *
 *  A dropdown child (`indent`) renders as a plain `Link`, not a
 *  `NavLink`, so the active child fills but does NOT paint the left-edge
 *  capsule — that capsule belongs to the parent group header instead
 *  (`aria-current` lives on the parent). Top-level rows stay as
 *  `NavLink` so each owns its own capsule. */
function NavLeaf({ to, label, icon: Icon, end, collapsed, indent = false }) {
  const { pathname } = useLocation()
  // Anchor for the portal-rendered collapsed-mode tooltip — escapes
  // the parent `<nav>`'s `overflow-y-auto` clipping context so the
  // tooltip can extend past the 68px-wide rail.
  const wrapperRef = useRef(null)

  const rowClass = (isActive) =>
    `group flex items-center gap-3 rounded-[10px] transition-colors cursor-pointer ${
      collapsed
        ? 'px-0 py-2.5 justify-center'
        : indent
          ? 'pl-9 pr-3 py-2.5'
          : 'px-3 py-2.5'
    } ${
      isActive
        ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
        : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white'
    }`

  const inner = (isActive) => (
    <>
      <Icon
        size={indent ? 16 : 18}
        className={
          isActive
            ? 'text-white shrink-0'
            : 'text-[color:var(--color-ink-muted)] group-hover:text-white shrink-0 transition-colors'
        }
        strokeWidth={isActive ? 2.25 : 1.75}
      />
      {collapsed ? null : <span className="truncate">{label}</span>}
    </>
  )

  // No `title` attribute — would render alongside the portal
  // tooltip (the browser's native title fires after a ~1.5s hover
  // delay, so users would see two floating labels). `aria-label`
  // when collapsed gives screen readers the accessible name without
  // a native tooltip; when expanded the visible text inside the
  // row provides accessibility on its own.
  if (indent) {
    const isActive = pathname.startsWith(to)
    return (
      <div ref={wrapperRef}>
        <Link
          to={to}
          aria-label={collapsed ? label : undefined}
          className={rowClass(isActive)}
        >
          {inner(isActive)}
        </Link>
        {collapsed ? <CollapsedNavTooltip label={label} anchorRef={wrapperRef} /> : null}
      </div>
    )
  }

  return (
    <div ref={wrapperRef}>
      <NavLink
        to={to}
        end={end}
        aria-label={collapsed ? label : undefined}
        className={({ isActive }) => rowClass(isActive)}
      >
        {({ isActive }) => inner(isActive)}
      </NavLink>
      {collapsed ? <CollapsedNavTooltip label={label} anchorRef={wrapperRef} /> : null}
    </div>
  )
}

/** Collapsible parent row + its children. Open/closed state is owned by
 *  the layout so the dropdowns behave as an accordion (only one open at
 *  a time); `open` + `onToggle` are passed in. The parent reads white +
 *  semibold while a child is active so the section stays legible when
 *  collapsed shut. When a child is active the header carries the
 *  left-edge capsule + accent fill (`aria-current`); the active child
 *  itself fills but stays capsule-free, so the marker reads as "you are
 *  in this section". */
function NavDropdown({ item, collapsed, open, onToggle }) {
  const Icon = item.icon
  const { pathname } = useLocation()
  const hasActiveChild = item.children.some((c) => pathname.startsWith(c.to))
  const wrapperRef = useRef(null)

  return (
    <div className="space-y-1.5">
      <button
        ref={wrapperRef}
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-current={hasActiveChild ? 'page' : undefined}
        // `aria-label` (collapsed only) instead of `title` so the
        // portal tooltip below isn't accompanied by the browser's
        // native title tooltip (~1.5s delay).
        aria-label={collapsed ? item.label : undefined}
        className={
          `group flex w-full items-center gap-3 rounded-[10px] transition-colors cursor-pointer hover:bg-[color:var(--color-card-hover)] hover:text-white ${
            collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'
          } ${
            hasActiveChild
              ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
              : 'text-[color:var(--color-ink-muted)]'
          }`
        }
      >
        <Icon
          size={18}
          className={
            hasActiveChild
              ? 'text-white shrink-0'
              : 'text-[color:var(--color-ink-muted)] group-hover:text-white shrink-0 transition-colors'
          }
          strokeWidth={hasActiveChild ? 2.25 : 1.75}
        />
        {collapsed ? null : (
          <>
            <span className="truncate flex-1 text-left">{item.label}</span>
            <ChevronDown
              size={16}
              className={'shrink-0 transition-transform ' + (open ? 'rotate-180' : '')}
            />
          </>
        )}
      </button>
      {open
        ? item.children.map((c) => (
            <NavLeaf
              key={c.to}
              to={c.to}
              label={c.label}
              icon={c.icon}
              collapsed={collapsed}
              indent={!collapsed}
            />
          ))
        : null}
      {/* Collapsed-sidebar hover tooltip — same portal-based
       *  treatment as `NavLeaf`. Anchored to the group HEADER BUTTON,
       *  not the wrapper div: the wrapper also contains the child rows
       *  in the collapsed rail (this layout renders them even when
       *  collapsed), so a wrapper anchor made hovering a child fire
       *  BOTH its own tooltip and the group's — two overlapping pills
       *  ("Billing" over "Payments", QA-reported). */}
      {collapsed ? <CollapsedNavTooltip label={item.label} anchorRef={wrapperRef} /> : null}
    </div>
  )
}

export function SuperAdminLayout() {
  const { user, signOut } = useAuthStore()
  const navigate = useNavigate()
  const collapsed = useSidebarCollapsed()
  // Mobile off-canvas drawer state (shared store); closes on navigation.
  const mobileOpen = useSidebarStore((s) => s.mobileOpen)
  const openMobile = useSidebarStore((s) => s.openMobile)
  const closeMobile = useSidebarStore((s) => s.closeMobile)
  const { pathname } = useLocation()
  const mainRef = useRef(null)
  useEffect(() => {
    closeMobile()
    // Reset the scroll container to the top on navigation. Without this a
    // deep-scrolled page (e.g. the Dashboard's Quick Actions at the bottom)
    // carries its scroll offset into the next route, landing the user at the
    // bottom of the Companies list instead of its start. Mirrors UserLayout.
    mainRef.current?.scrollTo({ top: 0, left: 0 })
  }, [pathname, closeMobile])

  // Accordion: only one nav dropdown is open at a time. The open
  // dropdown is tracked by its label here in the parent (rather than
  // each NavDropdown owning its own state) so opening one collapses the
  // others. Seeded to the dropdown that owns the current route, and the
  // effect re-opens that group whenever navigation lands inside a
  // different one, so a deep link / bell jump always arrives expanded.
  const dropdownKeyForPath = NAV.flatMap((g) => g.items)
    .filter((i) => i.children)
    .find((i) => i.children.some((c) => pathname.startsWith(c.to)))?.label
  const [openGroup, setOpenGroup] = useState(dropdownKeyForPath ?? null)
  useEffect(() => {
    if (dropdownKeyForPath) setOpenGroup(dropdownKeyForPath)
  }, [dropdownKeyForPath])

  async function handleSignOut() {
    await signOut()
    toast.success('Signed out successfully.')
    navigate('/sign-in', { replace: true })
  }

  return (
    <div className="taskmagix-page-bg h-screen flex overflow-hidden">
      {/* Mobile drawer backdrop — taps to close. Hidden on desktop (lg+). */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      ) : null}
      <aside
        className={
          'taskmagix-sidebar shrink-0 border-r border-[color:var(--color-rule)] text-[color:var(--color-ink)] text-[length:calc(var(--font-size-scale,1)*14px)] font-medium flex flex-col ' +
          // Mobile: off-canvas drawer; desktop (lg+): exact in-flow sidebar
          // via lg: resets, so desktop is unchanged.
          'fixed inset-y-0 left-0 z-50 lg:relative lg:inset-auto lg:z-auto ' +
          'transition-[transform,width] duration-200 ease-out ' +
          (mobileOpen ? 'translate-x-0 ' : '-translate-x-full ') +
          'lg:translate-x-0 ' +
          (collapsed ? 'w-[68px]' : 'w-[280px]')
        }
      >
        {/* Edge toggle — floating circle on the sidebar's right edge.
         *  Standing rule (matches AdminLayout + UserLayout): the
         *  toggle straddles the sidebar boundary so the header
         *  reads as "logo only" when expanded and "icon rail" when
         *  collapsed, without the toggle competing for either's
         *  real estate. */}
        <SidebarToggle placement="edge" />

        {/* Header — logo when expanded, empty when collapsed (full
         *  logo wouldn't fit at 68px). The header height stays 76px
         *  in both states so the topbar alignment with the right-
         *  side content header doesn't jump. */}
        <div
          className={
            'h-[84px] flex items-center justify-center border-b border-[color:var(--color-rule)] ' +
            (collapsed ? 'px-2' : 'px-4')
          }
        >
          {/* Collapsed rail keeps a fitted logo + go-home target — same
           *  treatment as UserLayout (see comment there). */}
          {collapsed ? (
            <Link
              to="/super-admin/dashboard"
              aria-label="Go to Dashboard"
              className="inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <BrandMarkIcon />
            </Link>
          ) : (
            <Link
              to="/super-admin/dashboard"
              aria-label="Go to Dashboard"
              className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <Logo size="xl" className="p-1.5" />
            </Link>
          )}
        </div>

        {/* Nav chrome matches AdminLayout / UserLayout exactly:
         *    px-4 horizontal, pt-6 top, space-y-1.5 between rows,
         *    min-h-0 so the column shrinks correctly inside the flex
         *    parent, and the same custom thin scrollbar (1.5px rail,
         *    ruled thumb, hover bump). Standing rule: any change to
         *    sidebar chrome happens in all three layouts in one go. */}
        <nav
          className={
            // Top fade-mask: rows fade to transparent before reaching the
            // header divider, so a scrolled list never reads as "touching"
            // the line under the logo. The fade height matches pt-6 (24px)
            // so at rest the first row sits clear of it and nothing looks
            // dimmed. Standing rule: this mask lives identically in all
            // three sidebar layouts (Admin / User / SuperAdmin).
            'flex-1 min-h-0 overflow-y-auto pt-6 pb-4 space-y-1.5 [mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [scrollbar-width:thin] [scrollbar-color:var(--color-rule)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[color:var(--color-rule)] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[color:var(--color-ink-muted)] ' +
            (collapsed ? 'px-2' : 'px-4')
          }
        >
          {NAV.map((group, gi) => (
            <div
              key={gi}
              className={
                group.title
                  ? 'mt-2 pt-3 border-t border-[color:var(--color-rule)] space-y-1.5'
                  : 'space-y-1.5'
              }
            >
              {/* Section header — hidden in collapsed mode (a label-
               *  only header in an icon-only column reads as orphan
               *  text). The divider stays so groups remain visually
               *  separated even when text is gone. */}
              {group.title && !collapsed ? (
                <p className="px-3 mb-2 text-[length:calc(var(--font-size-scale,1)*12px)] uppercase tracking-[0.12em] font-semibold text-[color:var(--color-ink-subtle)]">
                  {group.title}
                </p>
              ) : null}
              {group.items.map((item) =>
                item.children ? (
                  <NavDropdown
                    key={item.label}
                    item={item}
                    collapsed={collapsed}
                    open={openGroup === item.label}
                    onToggle={() =>
                      setOpenGroup((prev) =>
                        prev === item.label ? null : item.label,
                      )
                    }
                  />
                ) : (
                  <NavLeaf
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    end={item.end}
                    collapsed={collapsed}
                  />
                ),
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 text-[color:var(--color-ink)]">
        <header
          className="h-[64px] border-b border-[color:var(--color-rule)] px-4 lg:px-8 flex items-center justify-between gap-2 bg-[color:var(--color-card)]"
          style={{ fontSize: 'calc(var(--text-sm) + 1px)' }}
        >
          {/* Left group — live platform clock pinned to the far left so
           *  the eye reads the current date/time first. The (currently
           *  hidden) language selector also lives here as a global,
           *  app-wide control, separate from the right-side cluster. */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {/* Hamburger — opens the sidebar drawer on mobile / tablet;
             *  hidden on desktop (lg+). */}
            <button
              type="button"
              onClick={openMobile}
              aria-label="Open menu"
              className="lg:hidden -ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Menu size={20} />
            </button>
            {/* Live date/time pill. Hidden on mobile / tablet (widest header
             *  item, informational); shown on desktop (lg+) as before. */}
            <div className="hidden lg:block">
              <DateTimeClock />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            {/* System-level notifications bell — surfaces platform
             *  events (support tickets, plan-cap alerts, integration
             *  sync errors) the same way the Company Admin + User
             *  panel bells do. Same component, same endpoint; the
             *  payload is whatever notify() has fanned to this user. */}
            {/* Accessibility text-size control — Compact / Medium /
             *  Large. Sits before the bell so the most frequently
             *  used controls (notifications, profile) stay in the
             *  rightmost two slots where the eye expects them, and
             *  the lower-frequency accessibility toggle reads as an
             *  optional setting. */}
            <ThemeToggle />
            <FontSizeMenu />
            <NotificationsBell />
            <div
              className="h-8 w-px bg-[color:var(--color-rule)]"
              aria-hidden="true"
            />
            {user ? <UserMenu user={user} onSignOut={handleSignOut} /> : null}
          </div>
        </header>
        <main ref={mainRef} className="app-main-scroll flex-1 overflow-auto bg-[color:var(--color-bg)]">
          <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
